import React from 'react';
import { Search, Compass, Rocket, LineChart, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { processSteps } from '../data/mock';

const iconMap = {
  'search': Search,
  'compass': Compass,
  'rocket': Rocket,
  'line-chart': LineChart
};

const Process = () => {
  const handleConsultation = () => {
    window.open('https://wa.me/918185882299?text=Hi! I would like to learn more about your process.', '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-20">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Our <span className="bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">Process</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            A proven, systematic approach that transforms your business through strategic planning, precise execution, and continuous optimization
          </p>
        </div>

        {/* Process Timeline */}
        <div className="relative mb-32">
          {/* Connection Line - Desktop */}
          <div className="hidden lg:block absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500 via-purple-500 to-cyan-500"></div>

          {/* Steps */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => {
              const IconComponent = iconMap[step.icon];
              return (
                <div key={step.id} className="relative">
                  {/* Connection Line - Mobile */}
                  {index < processSteps.length - 1 && (
                    <div className="lg:hidden absolute left-12 top-32 bottom-0 w-1 bg-gradient-to-b from-cyan-500 to-purple-500"></div>
                  )}

                  <Card className="bg-gradient-to-br from-gray-900 to-black border border-cyan-500/30 p-8 rounded-3xl hover:border-cyan-400 transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-500/30 hover:scale-105 relative z-10">
                    {/* Step Number Badge */}
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-16 h-16 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg shadow-cyan-500/50 border-4 border-black">
                      <span className="text-2xl font-bold text-white">{step.id}</span>
                    </div>

                    {/* Icon */}
                    <div className="flex justify-center mb-6 mt-8">
                      <div className="p-4 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-2xl border border-cyan-500/30">
                        {IconComponent && <IconComponent size={40} className="text-cyan-400" />}
                      </div>
                    </div>

                    {/* Content */}
                    <h3 className="text-2xl font-bold text-white mb-4 text-center">
                      {step.title}
                    </h3>
                    <p className="text-gray-400 leading-relaxed text-center">
                      {step.description}
                    </p>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>

        {/* Detailed Process Breakdown */}
        <div className="space-y-16 mb-32">
          {/* Step 1 Details */}
          <Card className="bg-gradient-to-br from-gray-900 to-black border border-cyan-500/30 p-10 rounded-3xl hover:border-cyan-400 transition-all duration-300">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg shadow-cyan-500/50">
                    <span className="text-xl font-bold text-white">1</span>
                  </div>
                  <h3 className="text-3xl font-bold text-white">Understanding Business</h3>
                </div>
                <p className="text-gray-400 text-lg leading-relaxed mb-6">
                  We start by diving deep into your business, understanding your goals, challenges, and unique value proposition.
                </p>
                <ul className="space-y-3 text-gray-400">
                  <li className="flex items-start space-x-3">
                    <span className="text-cyan-400 mt-1">•</span>
                    <span>Comprehensive business analysis</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-cyan-400 mt-1">•</span>
                    <span>Target audience identification</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-cyan-400 mt-1">•</span>
                    <span>Competitive landscape research</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-cyan-400 mt-1">•</span>
                    <span>Current performance audit</span>
                  </li>
                </ul>
              </div>
              <div className="rounded-2xl overflow-hidden shadow-2xl shadow-cyan-500/30">
                <img
                  src="https://images.unsplash.com/photo-1694903089438-bf28d4697d9a"
                  alt="Understanding Business"
                  className="w-full h-80 object-cover"
                />
              </div>
            </div>
          </Card>

          {/* Step 2 Details */}
          <Card className="bg-gradient-to-br from-gray-900 to-black border border-purple-500/30 p-10 rounded-3xl hover:border-purple-400 transition-all duration-300">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 rounded-2xl overflow-hidden shadow-2xl shadow-purple-500/30">
                <img
                  src="https://images.unsplash.com/photo-1696041756040-c910a971f222"
                  alt="Strategy & Positioning"
                  className="w-full h-80 object-cover"
                />
              </div>
              <div className="order-1 lg:order-2">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full flex items-center justify-center shadow-lg shadow-purple-500/50">
                    <span className="text-xl font-bold text-white">2</span>
                  </div>
                  <h3 className="text-3xl font-bold text-white">Strategy & Positioning</h3>
                </div>
                <p className="text-gray-400 text-lg leading-relaxed mb-6">
                  We craft a customized strategy that positions your brand for maximum impact and competitive advantage.
                </p>
                <ul className="space-y-3 text-gray-400">
                  <li className="flex items-start space-x-3">
                    <span className="text-purple-400 mt-1">•</span>
                    <span>Custom growth strategy development</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-purple-400 mt-1">•</span>
                    <span>Brand positioning and messaging</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-purple-400 mt-1">•</span>
                    <span>Channel selection and optimization</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-purple-400 mt-1">•</span>
                    <span>Budget allocation and timeline planning</span>
                  </li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Step 3 Details */}
          <Card className="bg-gradient-to-br from-gray-900 to-black border border-cyan-500/30 p-10 rounded-3xl hover:border-cyan-400 transition-all duration-300">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg shadow-cyan-500/50">
                    <span className="text-xl font-bold text-white">3</span>
                  </div>
                  <h3 className="text-3xl font-bold text-white">Execution</h3>
                </div>
                <p className="text-gray-400 text-lg leading-relaxed mb-6">
                  With strategy in place, we execute flawlessly across all channels with precision and expertise.
                </p>
                <ul className="space-y-3 text-gray-400">
                  <li className="flex items-start space-x-3">
                    <span className="text-cyan-400 mt-1">•</span>
                    <span>Google Ads & Meta Ads campaign launch</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-cyan-400 mt-1">•</span>
                    <span>Website development and optimization</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-cyan-400 mt-1">•</span>
                    <span>Content creation and distribution</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-cyan-400 mt-1">•</span>
                    <span>Social media management and GBP optimization</span>
                  </li>
                </ul>
              </div>
              <div className="rounded-2xl overflow-hidden shadow-2xl shadow-cyan-500/30">
                <img
                  src="https://images.unsplash.com/photo-1677442135703-1787eea5ce01"
                  alt="Execution"
                  className="w-full h-80 object-cover"
                />
              </div>
            </div>
          </Card>

          {/* Step 4 Details */}
          <Card className="bg-gradient-to-br from-gray-900 to-black border border-purple-500/30 p-10 rounded-3xl hover:border-purple-400 transition-all duration-300">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 rounded-2xl overflow-hidden shadow-2xl shadow-purple-500/30">
                <img
                  src="https://images.unsplash.com/photo-1523961131990-5ea7c61b2107"
                  alt="Optimization & Growth"
                  className="w-full h-80 object-cover"
                />
              </div>
              <div className="order-1 lg:order-2">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-cyan-600 rounded-full flex items-center justify-center shadow-lg shadow-purple-500/50">
                    <span className="text-xl font-bold text-white">4</span>
                  </div>
                  <h3 className="text-3xl font-bold text-white">Optimization & Growth</h3>
                </div>
                <p className="text-gray-400 text-lg leading-relaxed mb-6">
                  We continuously monitor, test, and optimize every element to maximize performance and scale results.
                </p>
                <ul className="space-y-3 text-gray-400">
                  <li className="flex items-start space-x-3">
                    <span className="text-purple-400 mt-1">•</span>
                    <span>Performance tracking and analytics</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-purple-400 mt-1">•</span>
                    <span>A/B testing and optimization</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-purple-400 mt-1">•</span>
                    <span>Scaling successful campaigns</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-purple-400 mt-1">•</span>
                    <span>Regular reporting and strategy refinement</span>
                  </li>
                </ul>
              </div>
            </div>
          </Card>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <Card className="bg-gradient-to-r from-cyan-900/40 to-purple-900/40 border border-cyan-500/50 p-12 rounded-3xl backdrop-blur-sm shadow-2xl shadow-cyan-500/30">
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Start Your Growth Journey?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Let's implement our proven process to transform your business and achieve your goals
            </p>
            <Button
              onClick={handleConsultation}
              className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white px-10 py-6 text-lg rounded-full font-semibold shadow-2xl shadow-cyan-500/50 hover:shadow-cyan-500/70 transition-all duration-300 hover:scale-105"
            >
              Get Free Consultation <ArrowRight className="ml-2" size={20} />
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Process;
